import { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";

export interface UserPermissions {
  dashboard: boolean;
  planning: boolean;
  buying: boolean;
  measurement: boolean;
  powerCalculator: boolean;
  performanceAgent: boolean;
  mediaChannels: boolean;
  crmChannels: boolean;
  ecommerceChannels: boolean;
  rctExperiment: boolean;
  causalLoop: boolean;
}

interface JWTPayload {
  tenant_id?: string;
  tenantId?: string;
  [key: string]: any;
}

// Tenant-based permission configurations
const TENANT_PERMISSIONS: Record<string, UserPermissions> = {
  // Jope tenant UUID - only measurement access
  "544189b1-c029-4dd6-b651-15207fccfe76": {
    dashboard: true, // Dashboard visible but will show only measurement results
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
// Restore tenant UUID - only measurement access
  "44a49f83-cfd4-47ef-aca3-87bf19d97494": {
    dashboard: true, // Dashboard visible but will show only measurement results
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
"7563c07c-2572-4cc2-b14b-1d43b6010a78": {
    dashboard: true, // Dashboard visible but will show only measurement results
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  "31bb6772-77b5-4142-bcfc-04ff26927b54": {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  // Admin UUID (used for stage and ops, differentiated by hostname)
  "00000000-0000-0000-0000-000000000000": {
    dashboard: true,
    planning: true,
    buying: true,
    measurement: true,
    powerCalculator: true,
    performanceAgent: true,
    mediaChannels: true,
    crmChannels: true,
    ecommerceChannels: true,
    rctExperiment: true,
    causalLoop: true,
  },

  // Universal Ads Permissions
  "2e332294-8271-4a0e-8391-8b635f86a27e": {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  // NBCUniversal Permissions
  "2bccf661-3b86-4839-884b-921268d0a94e": {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: false,
    crmChannels: false,
    ecommerceChannels: false,
    rctExperiment: true,
    causalLoop: true,
  },

  // Legacy string-based keys for backward compatibility
  jope: {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  restore: {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  mightier: {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  stage: {
    dashboard: true,
    planning: true,
    buying: true,
    measurement: true,
    powerCalculator: true,
    performanceAgent: true,
    mediaChannels: true,
    crmChannels: true,
    ecommerceChannels: true,
    rctExperiment: true,
    causalLoop: true,
  },
  ops: {
    dashboard: true,
    planning: true,
    buying: true,
    measurement: true,
    powerCalculator: true,
    performanceAgent: true,
    mediaChannels: true,
    crmChannels: true,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  efile: {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  ua: {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: true,
    crmChannels: false,
    ecommerceChannels: true,
    rctExperiment: false,
    causalLoop: false,
  },
  nbcu: {
    dashboard: true,
    planning: false,
    buying: false,
    measurement: true,
    powerCalculator: false,
    performanceAgent: false,
    mediaChannels: false,
    crmChannels: false,
    ecommerceChannels: false,
    rctExperiment: true,
    causalLoop: true,
  },
};

// Default permissions for unknown tenants (restrictive)
const DEFAULT_PERMISSIONS: UserPermissions = {
  dashboard: true,
  planning: false,
  buying: false,
  measurement: false,
  powerCalculator: false,
  performanceAgent: false,
  mediaChannels: false,
  crmChannels: false,
  ecommerceChannels: false,
  rctExperiment: false,
  causalLoop: false,
};

const getTenantFromToken = (): string | null => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return null;

    const decoded = jwtDecode<JWTPayload>(token);
    // Support both tenant_id and tenantId for flexibility
    const tenantId = decoded.tenant_id || decoded.tenantId;

    if (!tenantId) return null;

    // Normalize tenant ID to lowercase for matching
    return tenantId?.toString()?.trim()?.toLowerCase();
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

export const usePermissions = () => {
  const [permissions, setPermissions] = useState<UserPermissions>(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);

      // Check if running on localhost
      const isLocalhost = window.location.hostname === "localhost" ||
                         window.location.hostname === "127.0.0.1";

      // Get tenant ID from JWT token
      const tenant = getTenantFromToken();
      setTenantId(tenant);

      // Debug log
      console.log("🔐 Permission Debug:", {
        tenant,
        isLocalhost,
        hostname: window.location.hostname
      });

      // Get permissions based on tenant
      let tenantPermissions = DEFAULT_PERMISSIONS;

      // If localhost, grant full permissions (stage-like access)
      if (isLocalhost) {
        console.log("✅ Localhost detected - granting full permissions");
        tenantPermissions = TENANT_PERMISSIONS.stage;
      } else if (tenant) {
        // Try to find permissions by exact tenant UUID match first
        if (TENANT_PERMISSIONS[tenant]) {
          tenantPermissions = TENANT_PERMISSIONS[tenant];
          // Special logging for known UUIDs
          if (tenant === "7563c07c-2572-4cc2-b14b-1d43b6010a78") {
            console.log("✅ Jope tenant UUID detected - restricted to measurement only");
          }
          else if (tenant === "44a49f83-cfd4-47ef-aca3-87bf19d97494") {
          }
          else if (tenant === "44a49f83-cfd4-47ef-aca3-87bf19d97494") {
            console.log("✅ Restore tenant UUID detected - restricted to measurement only");

          }

          else if (tenant === "00000000-0000-0000-0000-000000000000") {
            console.log("✅ Admin tenant UUID detected - granting full permissions");
          } else if (tenant === "44a49f83-cfd4-47ef-aca3-87bf19d97494") {
            console.log("✅ Restore tenant UUID detected - restricted to measurement only");

          }

          else if (tenant === "00000000-0000-0000-0000-000000000000") {
            console.log("✅ Admin tenant UUID detected - granting full permissions");
          } else if (tenant === "44a49f83-cfd4-47ef-aca3-87bf19d97494") {
            console.log("✅ Restore tenant UUID detected - restricted to measurement only");
          } else if (tenant.trim() === "31bb6772-77b5-4142-bcfc-04ff26927b54".trim()) {
            console.log("✅ Efile tenant UUID detected - restricted to measurement only");
          } else if (tenant.trim() === "2e332294-8271-4a0e-8391-8b635f86a27e".trim()) {
            console.log("✅ Universal Ads tenant UUID detected - restricted to measurement only");
          } else {
            console.log("✅ Tenant UUID matched:", tenant);
          }
        }
        // Fallback: Check if tenant name contains known tenant identifiers
        else if (tenant.includes("jope")) {
          console.log("✅ Jope tenant detected (by name) - restricted to measurement only");
          tenantPermissions = TENANT_PERMISSIONS.jope;
        }
        else if (tenant.includes("restore")) {
          console.log("✅ Restore tenant detected (by name) - restricted to measurement only");
          tenantPermissions = TENANT_PERMISSIONS.restore;
        } else if (tenant.includes("stage")) {
          console.log("✅ Stage tenant detected (by name) - granting full permissions");
          tenantPermissions = TENANT_PERMISSIONS.stage;
        } else if (tenant.includes("ops")) {
          console.log("✅ Ops tenant detected (by name) - granting full permissions");
          tenantPermissions = TENANT_PERMISSIONS.ops;
        } else if (tenant.includes("efile")) {
          console.log("✅ Efile tenant detected (by name) - granting full permissions");
          tenantPermissions = TENANT_PERMISSIONS.efile;
        } else if (tenant.includes("ua")) {
          console.log("✅ Universal Ads tenant detected (by name) - granting full permissions");
          tenantPermissions = TENANT_PERMISSIONS.ua;
        } else {
          console.log("⚠️ Unknown tenant UUID:", tenant, "- using default restrictive permissions");
          tenantPermissions = DEFAULT_PERMISSIONS;
        }
      } else {
        console.log("❌ No tenant ID found in token - using default restrictive permissions");
      }

      // In production, this could be replaced with an API call:
      // const response = await axiosInstance('/api/user/permissions');
      // const data = await response.json();
      // setPermissions(data);

      setPermissions(tenantPermissions);
      setLoading(false);
    };

    fetchPermissions();
  }, []);

  const hasPermission = useCallback((permissionKey: keyof UserPermissions): boolean => {
    return permissions[permissionKey] || false;
  }, [permissions]);

  const checkMenuPermission = useCallback((path: string): boolean => {
    // Map paths to permission keys
    const pathPermissionMap: Record<string, keyof UserPermissions> = {
      "/": "dashboard",
      "/planning/marketing-mix-model-landing": "planning",
      "/optimization/landing": "buying",
      "/measurement/geo-lift": "measurement",
      "/power-calculator/landing": "powerCalculator",
      "/rct-experiment/landing": "rctExperiment",
      "/causal-loop/landing": "causalLoop",
      "/agent": "performanceAgent",
      "/facebook/integrations": "mediaChannels",
      "/crm/integrations": "crmChannels",
      "/e-commerce/integrations": "ecommerceChannels",
    };

    const permissionKey = pathPermissionMap[path];
    return permissionKey ? permissions[permissionKey] || false : true;
  }, [permissions]);

  return {
    permissions,
    loading,
    hasPermission,
    checkMenuPermission,
    tenantId,
  };
};
